import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { ArrowRight, Workflow, MessageSquare, Lightbulb, Code, Rocket, Headphones } from 'lucide-react';

const processSteps = [
  {
    icon: MessageSquare,
    number: '01',
    title: 'Discovery & Consultation',
    description: 'We start by understanding your business, goals, target audience, and challenges through in-depth discussions and research. This foundation ensures we create solutions that truly address your needs.',
    deliverables: ['Initial consultation', 'Requirements gathering', 'Competitor analysis', 'Project scope definition']
  },
  {
    icon: Lightbulb,
    number: '02',
    title: 'Strategy & Planning',
    description: 'Based on our discovery findings, we develop a comprehensive strategy and detailed project roadmap. Every decision is data-driven and aligned with your business objectives.',
    deliverables: ['Project timeline', 'Technical specifications', 'Information architecture', 'Resource allocation']
  },
  {
    icon: Code,
    number: '03',
    title: 'Design & Development',
    description: 'Our team brings your vision to life using cutting-edge design and development practices. We maintain open communication throughout, with regular updates and opportunities for feedback.',
    deliverables: ['UI/UX designs', 'Development sprints', 'Regular progress updates', 'Feedback integration']
  },
  {
    icon: Rocket,
    number: '04',
    title: 'Testing & Launch',
    description: 'Before launch, we conduct rigorous testing to ensure everything works perfectly across all devices and scenarios. Then we handle the launch process to ensure a smooth go-live.',
    deliverables: ['Quality assurance', 'Cross-browser testing', 'Performance optimization', 'Launch coordination']
  },
  {
    icon: Headphones,
    number: '05',
    title: 'Support & Growth',
    description: 'Our relationship doesn\'t end at launch. We provide ongoing support, maintenance, and optimization to ensure your digital assets continue to perform and grow.',
    deliverables: ['Post-launch support', 'Performance monitoring', 'Continuous optimization', 'Growth strategies']
  }
];

export default function ProcessPage() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <Badge variant="secondary" className="mb-6">
              <Workflow className="h-4 w-4 mr-2" />
              Our Process
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground mb-6">
              How We{' '}
              <span className="gradient-text">Work</span>
            </h1>
            <p className="text-lg text-muted-foreground">
              Our proven process ensures every project is delivered on time, on budget, and exceeds expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="space-y-16">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                  className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? '' : ''}`}
                >
                  <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-16 h-16 rounded-xl gradient-primary flex items-center justify-center">
                        <Icon className="h-8 w-8 text-primary-foreground" />
                      </div>
                      <div className="text-5xl font-display font-bold text-muted-foreground/20">
                        {step.number}
                      </div>
                    </div>
                    <h2 className="text-3xl font-display font-bold text-foreground mb-4">
                      {step.title}
                    </h2>
                    <p className="text-lg text-muted-foreground mb-6">
                      {step.description}
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      {step.deliverables.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <div className="w-2 h-2 rounded-full bg-primary" />
                          <span className="text-foreground">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <Card className="p-8 bg-gradient-to-br from-accent/50 to-accent/20 border-none">
                      <div className="aspect-[4/3] rounded-lg bg-card flex items-center justify-center">
                        <Icon className="h-24 w-24 text-primary/20" />
                      </div>
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-primary">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-primary-foreground/80 mb-8">
              Let's discuss how our process can work for you.
            </p>
            <Button variant="premium" size="lg" asChild className="bg-background text-foreground hover:bg-background/90">
              <Link to="/get-started">
                Get Started
                <ArrowRight className="h-5 w-5 ml-2" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
